import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { BrandAvatar } from "@faire/retailer-visitor-shared/components/BrandAvatar/index";
import { NoTranslate } from "@faire/retailer-visitor-shared/components/NoTranslate";
import { useTopShopVisibility } from "@faire/retailer-visitor-shared/components/TopShop/useTopShopVisibility";
import { isLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { trackBrandClick } from "@faire/retailer-visitor-shared/services/Events";
import { getSettingElevateTopShopV2 } from "@faire/retailer-visitor-shared/settings/getSettingElevateTopShopV2";
import { RetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { TopShopBadge } from "@faire/retailer-visitor-shared/views/brand/components/TopShopBadge";
import { generateBrandUrlForProductPage } from "@faire/retailer-visitor-shared/views/product/utils/generateBrandUrlForProductPage";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { formatMoney, moneyOrZero } from "@faire/web--source/common/money";
import { faireBaseUrl } from "@faire/web--source/common/routes/faireBaseUrl";
import { trackEventOnClick } from "@faire/web--source/common/trackEvent";
import { Button } from "@faire/web--source/slate/Button";
import { Star } from "@faire/web--source/slate/icons/foundation/Star";
import { Flex, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import React from "react";
import styled from "styled-components";
var LONG_BRAND_NAME_LENGTH = 20;
export var MaybeSimpleBrandTitle = function(param) {
    var brand = param.brand, product = param.product, brandReviewSummary = param.brandReviewSummary;
    if (!brand) {
        return null;
    }
    return /*#__PURE__*/ _jsx(SimpleBrandTitle, {
        brand: brand,
        product: product,
        brandReviewSummary: brandReviewSummary
    });
};
var SimpleBrandTitle = function(param) {
    var brand = param.brand, product = param.product, brandReviewSummary = param.brandReviewSummary;
    var _brand_name, _brand_profile_image, _brand_profile_image1;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var isTablet = useViewport().isTablet;
    var isTopShopContentVisibleForBrand = useTopShopVisibility(brand.is_top_shop).isTopShopContentVisibleForBrand;
    var minimumInfo = strictLocalize({
        id: "doMs11",
        defaultMessage: [
            {
                type: 1,
                value: "minimumOrderAmountMoney"
            },
            {
                type: 0,
                value: " min"
            }
        ]
    }, {
        minimumOrderAmountMoney: formatMoney({
            money: moneyOrZero(brand.minimum_order_amount)
        })
    });
    var onBrandClick = function() {
        if ((brand === null || brand === void 0 ? void 0 : brand.token) !== undefined) {
            trackBrandClick(brand.token, "product_page_simple_brand_title");
        }
    };
    var _product_original_brand_token;
    var token = (_product_original_brand_token = product.original_brand_token) !== null && _product_original_brand_token !== void 0 ? _product_original_brand_token : brand === null || brand === void 0 ? void 0 : brand.token;
    var _product_token;
    var brandPageUrl = token ? generateBrandUrlForProductPage(token, (_product_token = product.token) !== null && _product_token !== void 0 ? _product_token : "") : faireBaseUrl();
    var _brand_name_length;
    var brandNameLength = (_brand_name_length = (_brand_name = brand.name) === null || _brand_name === void 0 ? void 0 : _brand_name.length) !== null && _brand_name_length !== void 0 ? _brand_name_length : 0;
    var shouldShowBrandSummaryBelowBrandName = brandNameLength >= LONG_BRAND_NAME_LENGTH || isTablet || getSettingElevateTopShopV2();
    return /*#__PURE__*/ _jsxs(Row, {
        align: "center",
        children: [
            /*#__PURE__*/ _jsx(Link, {
                onClick: onBrandClick,
                to: brandPageUrl,
                children: ((_brand_profile_image = brand.profile_image) === null || _brand_profile_image === void 0 ? void 0 : _brand_profile_image.url) ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(BrandAvatar, {
                            image: brand.profile_image,
                            size: "smaller",
                            isTopShop: brand.is_top_shop,
                            iconSize: "22px",
                            displayTooltip: true,
                            hideIcon: getSettingElevateTopShopV2(),
                            hideTopShopBorder: getSettingElevateTopShopV2()
                        }),
                        /*#__PURE__*/ _jsx("meta", {
                            itemProp: "image",
                            content: (_brand_profile_image1 = brand.profile_image) === null || _brand_profile_image1 === void 0 ? void 0 : _brand_profile_image1.url
                        })
                    ]
                }) : null
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: "1x"
            }),
            /*#__PURE__*/ _jsxs(Wrapper, {
                $flexDirection: shouldShowBrandSummaryBelowBrandName ? "column" : "row",
                children: [
                    /*#__PURE__*/ _jsx(Row, {
                        align: "center",
                        children: /*#__PURE__*/ _jsx(Button, {
                            style: {
                                textDecoration: "none"
                            },
                            variant: "tertiary",
                            onClick: onBrandClick,
                            linkTo: brandPageUrl,
                            children: /*#__PURE__*/ _jsx(NoTranslate, {
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraphSansMedium",
                                    children: brand.name
                                })
                            })
                        })
                    }),
                    !shouldShowBrandSummaryBelowBrandName ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: 4
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                children: " · "
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: 4
                            })
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsxs(Row, {
                        align: "center",
                        children: [
                            isTopShopContentVisibleForBrand && getSettingElevateTopShopV2() ? /*#__PURE__*/ _jsx(CenterAlignedRow, {
                                children: /*#__PURE__*/ _jsx(TopShopBadge, {
                                    hideUnderline: true,
                                    iconSize: "12px",
                                    typographyVariant: "labelSansRegular"
                                })
                            }) : null,
                            /*#__PURE__*/ _jsx(MaybeBrandReviewSummary, {
                                product: product,
                                brandReviewSummary: brandReviewSummary
                            }),
                            /*#__PURE__*/ _jsx(MaybeBrandMinimum, {
                                minimumInfo: minimumInfo
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
var MaybeBrandMinimum = function(param) {
    var minimumInfo = param.minimumInfo;
    return isLoggedInRetailer() ? /*#__PURE__*/ _jsx(CenterAlignedRow, {
        children: /*#__PURE__*/ _jsx(Typography, {
            variant: "labelSansRegular",
            color: Core.text.default,
            children: minimumInfo
        })
    }) : null;
};
var MaybeBrandReviewSummary = function(param) {
    var product = param.product, brandReviewSummary = param.brandReviewSummary;
    if (!brandReviewSummary) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(CenterAlignedRow, {
        children: [
            /*#__PURE__*/ _jsx(Star, {
                fontSize: getSettingElevateTopShopV2() ? "12px" : "13px",
                fill: Core.icon.default,
                color: Core.icon.default
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: 4
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                variant: "labelSansRegular",
                children: [
                    brandReviewSummary.overallRatingText,
                    " (",
                    /*#__PURE__*/ _jsx(EmbeddedLink, {
                        linkTo: "#brand-reviews-summary",
                        onClick: trackEventOnClick("retailer/".concat(RetailerStore.get().retailerToken, "/product/").concat(product.token, "/see-reviews/clicked")),
                        children: brandReviewSummary.numberOfReviews
                    }),
                    ")"
                ]
            })
        ]
    });
};
var Wrapper = styled(Flex).withConfig({
    componentId: "sc-28e27801-0"
})([
    "flex-flow:",
    " wrap;"
], function(props) {
    return props.$flexDirection;
});
var CenterAlignedRow = styled(Row).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-28e27801-1"
})([
    '& + &{&:before{content:" ";background-color:',
    ";height:2px;width:2px;margin:0 6px;border-radius:50%;}}"
], Core.surface.default);
